import {
  FacetValuesList,
  UrlSearchState,
  SEARCH_QUERY_KEY,
  PAGE_QUERY_KEY,
  PRICE_SORT_QUERY_KEY,
  LOCAL_SEARCH_QUERY_KEY,
  FacetValue,
} from '@packages/common';

import { omit } from 'rambda';

/**
 * @description takes a list of facet values and a url state and checks for
 * missing facets in the list
 * @returns missing facets as `{ facetName: [facetValue1, facetValue2, ...] }`
 */
export function checkMissingFacetValues(
  facetValuesRecord: FacetValuesList,
  urlSearchState: UrlSearchState,
): Record<string, string[]> | undefined {
  const urlQueryFacets: Record<string, { value: string }[]> = omit(
    [SEARCH_QUERY_KEY, PAGE_QUERY_KEY, PRICE_SORT_QUERY_KEY, LOCAL_SEARCH_QUERY_KEY],
    urlSearchState,
  );

  // this holds facets that are present as url query param, but not in
  // `facetValuesRecord`. Key: facet-name, value: facet-value-name
  const missingFacetsBuffer: Record<string, string[]> = {};

  // helper function to add new data to the buffer / combine with existing data
  const bufferMissingFacet = (facetName: string, ...facetValueNames: string[]) => {
    // key already exists
    if (missingFacetsBuffer[facetName]) {
      missingFacetsBuffer[facetName] = missingFacetsBuffer[facetName].concat(facetValueNames);
    } else {
      missingFacetsBuffer[facetName] = facetValueNames;
    }
  };

  Object.keys(urlQueryFacets ?? {}).forEach((facetName) => {
    const curUrlFacetValues = urlQueryFacets[facetName];
    const facetValueFromList: FacetValue[] = facetValuesRecord?.[facetName];
    // the whole key is not present:
    if (!facetValueFromList) {
      const missingValues = curUrlFacetValues.map((data) => data.value);
      bufferMissingFacet(facetName, ...missingValues);
      // key is present - but potentially there are some missing values
    } else {
      const missingFacets = curUrlFacetValues
        .filter((urlFacetValue) => {
          return facetValueFromList.every((facetValue) => facetValue.seoName !== urlFacetValue.value);
        })
        .map((urlFacetData) => urlFacetData.value);

      if (missingFacets?.length > 0) {
        bufferMissingFacet(facetName, ...missingFacets);
      }
    }
  });

  return Object.keys(missingFacetsBuffer).length > 0 ? missingFacetsBuffer : undefined;
}
